import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, Star, Quote as QuoteIcon } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, LineChart, Line,
} from "recharts";
import { api, formatApiError } from "../../lib/api";

const TOOLTIP_STYLE = {
  background: "#031c18", border: "1px solid #1a4640",
  color: "#fdfbf7", fontSize: 12, borderRadius: 2,
};

const TABS = [
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Publicados" },
  { key: "rejected", label: "Rejeitados" },
  { key: "all", label: "Todos" },
];

export default function AdminTestimonials() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  async function loadStats() {
    try {
      const { data } = await api.get("/admin/testimonials-stats?months=6");
      setStats(data);
    } catch (_) { /* silent */ }
  }
  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/testimonials?status=${tab}`);
      setItems(data.items || []);
      setPendingCount(data.pending_count || 0);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { loadStats(); }, []);

  async function approve(id) {
    try {
      await api.post(`/admin/testimonials/${id}/approve`);
      toast.success("Publicado.");
      load(); loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function reject(id) {
    try {
      await api.post(`/admin/testimonials/${id}/reject`);
      toast.success("Rejeitado.");
      load(); loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function remove(id) {
    if (!window.confirm("Apagar definitivamente este testemunho?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Apagado.");
      load(); loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="space-y-8" data-testid="admin-testimonials">
      <div>
        <div className="eyebrow mb-3">Moderação · Vozes dos hosts</div>
        <h1 className="font-display text-5xl">Testemunhos.</h1>
      </div>

      <StatsDashboard stats={stats} />

      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                    tab === t.key
                      ? "bg-[#d4af37] text-[#031c18] border-[#d4af37]"
                      : "border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37]"
                  }`}
                  data-testid={`tab-${t.key}`}>
            {t.label}
            {t.key === "pending" && pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full bg-[#f59e0b] text-[#031c18] font-bold">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#a0b2af]">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="text-[#a0b2af] italic py-12 text-center">
          Sem testemunhos {tab !== "all" ? `em "${TABS.find(t => t.key === tab)?.label.toLowerCase()}"` : ""}.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map(t => (
            <article key={t.id}
                     className="lux-card p-5 space-y-4 relative"
                     data-testid={`testimonial-row-${t.id}`}>
              <QuoteIcon size={16} className="text-[#d4af37]/30 absolute top-4 right-4" />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={12}
                          fill={i <= (t.rating || 5) ? "#d4af37" : "transparent"}
                          stroke="#d4af37" />
                  ))}
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm ${
                  t.status === "pending" ? "border-[#f59e0b]/50 text-[#f59e0b]" :
                  t.status === "approved" ? "border-[#10b981]/50 text-[#10b981]" :
                  "border-[#a0b2af]/40 text-[#a0b2af]"
                }`}>
                  {t.status}
                </span>
              </div>
              <p className="text-sm leading-relaxed italic text-[#fdfbf7]/90">«{t.quote}»</p>
              <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] space-y-0.5">
                <div className="text-[#d4af37]">
                  {t.first_name || "Cliente"}
                  {t.accommodation && <span className="text-[#fdfbf7]/60"> · {t.accommodation}</span>}
                </div>
                <div>{t.region} · {(t.created_at || "").slice(0, 10)}</div>
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-[#1a4640]">
                {t.status !== "approved" && (
                  <button onClick={() => approve(t.id)}
                          className="btn-gold px-3 py-1.5 text-[11px] flex items-center gap-1"
                          data-testid={`approve-${t.id}`}>
                    <Check size={11} /> Publicar
                  </button>
                )}
                {t.status !== "rejected" && (
                  <button onClick={() => reject(t.id)}
                          className="btn-ghost-gold px-3 py-1.5 text-[11px] flex items-center gap-1"
                          data-testid={`reject-${t.id}`}>
                    <X size={11} /> Rejeitar
                  </button>
                )}
                <button onClick={() => remove(t.id)}
                        className="ml-auto text-[#a0b2af] hover:text-red-400 p-1.5"
                        title="Apagar permanentemente"
                        data-testid={`delete-${t.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}


function StatCard({ label, value, accent = "#d4af37", testId }) {
  return (
    <div className="lux-card p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className="font-display text-3xl mt-1.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function StatsDashboard({ stats }) {
  if (!stats) return null;
  const { totals = {}, series = [] } = stats;
  const hasData = series.some(s => s.submitted > 0);

  return (
    <section className="space-y-5" data-testid="testimonials-stats">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Recebidos (total)" value={totals.all || 0}
                  testId="stat-total" />
        <StatCard label="Pendentes" value={totals.pending || 0}
                  accent="#f59e0b" testId="stat-pending" />
        <StatCard label="Publicados" value={totals.approved || 0}
                  accent="#10b981" testId="stat-approved" />
        <StatCard label="Rejeitados" value={totals.rejected || 0}
                  accent="#a0b2af" testId="stat-rejected" />
        <StatCard label="Média ★ (publicados)"
                  value={(totals.avg_rating || 0).toFixed(2)}
                  testId="stat-avg" />
      </div>

      {!hasData ? (
        <div className="lux-card p-10 text-center text-[#a0b2af] italic"
             data-testid="stats-empty">
          Sem testemunhos nos últimos 6 meses para apresentar.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="lux-card p-5" data-testid="chart-volume">
            <div className="eyebrow mb-3">Volume mensal</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
                  <XAxis dataKey="month" tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#062c27" }} />
                  <Legend wrapperStyle={{ color: "#fdfbf7", fontSize: 12 }} />
                  <Bar dataKey="submitted" name="Recebidos" fill="#a0b2af" radius={[2,2,0,0]} />
                  <Bar dataKey="approved"  name="Publicados" fill="#d4af37" radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lux-card p-5" data-testid="chart-rating">
            <div className="eyebrow mb-3">Média de estrelas</div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a4640" />
                  <XAxis dataKey="month" tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <YAxis domain={[0, 5]} tick={{ fill: "#a0b2af", fontSize: 10 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="avg_rating" name="Média ★"
                        stroke="#d4af37" strokeWidth={2}
                        dot={{ r: 4, fill: "#d4af37" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
